// src/components/Schedule/TourVariablesSection.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  AlertTitle,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Tour as TourIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  DirectionsCar as TransportIcon,
  Hotel as HotelIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle,
  ErrorOutline as ErrorIcon,
  CloudSync,
  Restaurant as RestaurantIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, differenceInDays, addDays, parseISO } from 'date-fns';

interface TourVariablesSectionProps {
  variables: any;
  onVariableChange: (key: string, value: any) => void;
  errors?: Record<string, string>;
  isInternational?: boolean;
  onInternationalChange?: (value: boolean) => void;
  // Auto-save props
  scheduleId?: string;
  dbState?: {
    isSaving: boolean;
    saveStatus: 'saved' | 'saving' | 'error' | 'unsaved';
    lastSaved?: Date;
    version?: number;
  };
}

// Save Status Indicator Component
const SaveStatusIndicator = ({ dbState, scheduleId }: { dbState?: any; scheduleId?: string }) => {
  if (!scheduleId || !dbState) return null;

  const getStatusIcon = () => {
    switch (dbState.saveStatus) {
      case 'saved':
        return <CheckCircle sx={{ color: 'green', fontSize: 16 }} />;
      case 'saving':
        return <CircularProgress size={16} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'red', fontSize: 16 }} />;
      case 'unsaved':
        return <CloudSync sx={{ color: 'orange', fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (dbState.saveStatus) {
      case 'saved':
        return dbState.lastSaved ? `Saved ${format(dbState.lastSaved, 'HH:mm')}` : 'Saved';
      case 'saving':
        return 'Saving...';
      case 'error':
        return 'Save failed';
      case 'unsaved':
        return 'Unsaved changes';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {getStatusIcon()}
      <Typography variant="caption" sx={{ fontSize: 12 }}>
        {getStatusText()}
      </Typography>
    </Box>
  );
};

// Fast input component
const FastInput = React.memo(({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  disabled = false,
  error,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
}) => (
  <TextField
    fullWidth
    label={label}
    type={type}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    placeholder={placeholder}
    required={required}
    error={error}
    variant="outlined"
    size="small"
    sx={{
      '& .MuiInputBase-input': {
        fontSize: '0.9rem'
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.9rem'
      }
    }}
  />
));

// Fast select component
const FastSelect = React.memo(({
  label,
  value,
  onChange,
  options,
  required = false,
  error
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  error?: string;
}) => (
  <FormControl fullWidth size="small" error={!!error}>
    <InputLabel>{label}</InputLabel>
    <Select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      label={label}
      required={required}
    >
      {options.map(opt => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
));

// Venue tab content with progressive disclosure
const VenueTabContent = React.memo(({
  venueNumber,
  variables,
  onVariableChange,
  errors,
  uiFlags,
  numberOfShows,
  tourStartDate,
  tourEndDate
}: {
  venueNumber: number;
  variables: any;
  onVariableChange: (key: string, value: string) => void;
  errors: Record<string, string>;
  uiFlags: any;
  numberOfShows: number;
  tourStartDate?: Date;
  tourEndDate?: Date;
}) => {
  const getVenueValue = useCallback((field: string) => {
    const key = `VENUE_${venueNumber}_${field}`;
    return (variables as any)[key] || '';
  }, [variables, venueNumber]);

  const handleVenueChange = useCallback((field: string, val: string) => {
    const key = `VENUE_${venueNumber}_${field}`;
    onVariableChange(key, val);
  }, [onVariableChange, venueNumber]);

  // Route planning functions
  const getRouteValue = useCallback((toVenueNumber: number, field: string) => {
    const key = `VENUE_${venueNumber}_TO_VENUE_${toVenueNumber}_${field}`;
    return (variables as any)[key] || '';
  }, [variables, venueNumber]);

  const handleRouteChange = useCallback((toVenueNumber: number, field: string, val: string) => {
    const key = `VENUE_${venueNumber}_TO_VENUE_${toVenueNumber}_${field}`;
    onVariableChange(key, val);
  }, [onVariableChange, venueNumber]);

  // Progressive disclosure flags for this venue
  const [venueFlags, setVenueFlags] = useState(() => ({
    hasArtistLiaison: false,
    hasTransportContact: false,
    hasHotelContact: false,
    hasCateringContact: false,
    hasSecurityContact: false,
    hasMerchandisingContact: false,
    hasPromoterContact: false,
    hasProductionContact: false,
    hasMarketingContact: false,
    hasSpecialArrangements: false
  }));

  const updateVenueFlag = useCallback((flag: string, value: boolean) => {
    setVenueFlags(prev => ({ ...prev, [flag]: value }));
  }, []);

  const transportOptions = [
    { value: 'flight', label: 'Flight' },
    { value: 'train', label: 'Train' },
    { value: 'bus', label: 'Bus/Coach' },
    { value: 'car', label: 'Private Car' },
    { value: 'rental_car', label: 'Rental Car' },
    { value: 'taxi', label: 'Taxi/Transfer' },
    { value: 'ferry', label: 'Ferry' }
  ];

  const mealTypeOptions = [
    { value: 'venue_catering', label: 'Venue Catering' },
    { value: 'venue_restaurant', label: 'Venue Restaurant' },
    { value: 'buy_out', label: 'Buy Out (Cash)' },
    { value: 'per_diem', label: 'Per Diem Allowance' },
    { value: 'local_restaurant', label: 'Local Restaurant' },
    { value: 'promoter_provided', label: 'Promoter Provided' },
    { value: 'self_catered', label: 'Self Catered' }
  ];

  const venueName = getVenueValue('NAME');
  const venueCity = getVenueValue('CITY');
  const nextVenueCity = numberOfShows > venueNumber ? (variables as any)[`VENUE_${venueNumber + 1}_CITY`] : '';

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {/* Essential Fields */}
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
            Basic Information
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FastInput
            label="Venue Name"
            value={getVenueValue('NAME')}
            onChange={v => handleVenueChange('NAME', v)}
            required
            error={!!errors[`VENUE_${venueNumber}_NAME`]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FastInput
            label="City"
            value={getVenueValue('CITY')}
            onChange={v => handleVenueChange('CITY', v)}
            required
            error={!!errors[`VENUE_${venueNumber}_CITY`]}
          />
        </Grid>
        <Grid item xs={12}>
          <FastInput
            label="Address"
            value={getVenueValue('ADDRESS')}
            onChange={v => handleVenueChange('ADDRESS', v)}
          />
        </Grid>

        {/* Performance Details */}
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" sx={{ mb: 1, mt: 2 }}>
            Performance Details
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Performance Date"
              value={getVenueValue('PERFORMANCE_DATE') ? parseISO(getVenueValue('PERFORMANCE_DATE')) : null}
              onChange={(date) => handleVenueChange('PERFORMANCE_DATE', date ? format(date, 'yyyy-MM-dd') : '')}
              minDate={tourStartDate}
              maxDate={tourEndDate}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: !!errors[`VENUE_${venueNumber}_PERFORMANCE_DATE`]
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <FastInput
            label="Performance Time"
            type="time"
            value={getVenueValue('PERFORMANCE_TIME') || '20:00'}
            onChange={v => handleVenueChange('PERFORMANCE_TIME', v)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FastInput
            label="Soundcheck Time"
            type="time"
            value={getVenueValue('SOUNDCHECK_TIME') || '17:00'}
            onChange={v => handleVenueChange('SOUNDCHECK_TIME', v)}
          />
        </Grid>

        {/* Accommodation */}
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" sx={{ mb: 1, mt: 2 }}>
            <HotelIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            Accommodation
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FastInput
            label="Hotel Name"
            value={getVenueValue('HOTEL_NAME')}
            onChange={v => handleVenueChange('HOTEL_NAME', v)}
            required
            error={!!errors[`VENUE_${venueNumber}_HOTEL_NAME`]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FastInput
            label="Hotel Address"
            value={getVenueValue('HOTEL_ADDRESS')}
            onChange={v => handleVenueChange('HOTEL_ADDRESS', v)}
          />
        </Grid>

        {/* Meal Arrangements - Context Aware */}
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" sx={{ mb: 1, mt: 2 }}>
            <RestaurantIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            Meal Arrangements {venueCity && `for ${venueCity}`}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <FastSelect
            label="Lunch Type"
            value={getVenueValue('LUNCH_TYPE')}
            onChange={v => handleVenueChange('LUNCH_TYPE', v)}
            options={mealTypeOptions}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FastInput
            label="Lunch Location"
            value={getVenueValue('LUNCH_LOCATION')}
            onChange={v => handleVenueChange('LUNCH_LOCATION', v)}
            placeholder="Restaurant name or venue area"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FastInput
            label="Lunch Budget"
            value={getVenueValue('LUNCH_BUDGET')}
            onChange={v => handleVenueChange('LUNCH_BUDGET', v)}
            placeholder="€30 per person, etc."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FastSelect
            label="Dinner Type"
            value={getVenueValue('DINNER_TYPE')}
            onChange={v => handleVenueChange('DINNER_TYPE', v)}
            options={mealTypeOptions}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FastInput
            label="Dinner Location"
            value={getVenueValue('DINNER_LOCATION')}
            onChange={v => handleVenueChange('DINNER_LOCATION', v)}
            placeholder="Restaurant name, venue area, or arrangement details"
          />
        </Grid>

        {/* Contacts - Progressive Disclosure */}
        {!uiFlags.hasTourManager && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" sx={{ mb: 1, mt: 2 }}>
                <PeopleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Contacts
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FastInput
                label="Primary Contact Name"
                value={getVenueValue('PRIMARY_CONTACT_NAME')}
                onChange={v => handleVenueChange('PRIMARY_CONTACT_NAME', v)}
                required
                error={!!errors[`VENUE_${venueNumber}_PRIMARY_CONTACT_NAME`]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastInput
                label="Primary Contact Email"
                value={getVenueValue('PRIMARY_CONTACT_EMAIL')}
                onChange={v => handleVenueChange('PRIMARY_CONTACT_EMAIL', v)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastInput
                label="Primary Contact Phone"
                value={getVenueValue('PRIMARY_CONTACT_PHONE')}
                onChange={v => handleVenueChange('PRIMARY_CONTACT_PHONE', v)}
              />
            </Grid>

            {/* Tech Contact (if no Production Manager) */}
            {!uiFlags.hasProductionManager && (
              <>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Tech Contact Name"
                    value={getVenueValue('TECH_CONTACT_NAME')}
                    onChange={v => handleVenueChange('TECH_CONTACT_NAME', v)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Tech Contact Email"
                    value={getVenueValue('TECH_CONTACT_EMAIL')}
                    onChange={v => handleVenueChange('TECH_CONTACT_EMAIL', v)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Tech Contact Phone"
                    value={getVenueValue('TECH_CONTACT_PHONE')}
                    onChange={v => handleVenueChange('TECH_CONTACT_PHONE', v)}
                  />
                </Grid>
              </>
            )}

            {/* Progressive Contact Switches */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Additional Contacts (optional)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={venueFlags.hasArtistLiaison}
                      onChange={(e) => updateVenueFlag('hasArtistLiaison', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Artist Liaison"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={venueFlags.hasTransportContact}
                      onChange={(e) => updateVenueFlag('hasTransportContact', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Transport Contact"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={venueFlags.hasCateringContact}
                      onChange={(e) => updateVenueFlag('hasCateringContact', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Catering Contact"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={venueFlags.hasMarketingContact}
                      onChange={(e) => updateVenueFlag('hasMarketingContact', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Marketing Contact"
                />
              </Box>
            </Grid>

            {/* Conditional Contact Fields */}
            {venueFlags.hasArtistLiaison && (
              <>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Artist Liaison Name"
                    value={getVenueValue('ARTIST_LIAISON_NAME')}
                    onChange={v => handleVenueChange('ARTIST_LIAISON_NAME', v)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Artist Liaison Email"
                    value={getVenueValue('ARTIST_LIAISON_EMAIL')}
                    onChange={v => handleVenueChange('ARTIST_LIAISON_EMAIL', v)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Artist Liaison Phone"
                    value={getVenueValue('ARTIST_LIAISON_PHONE')}
                    onChange={v => handleVenueChange('ARTIST_LIAISON_PHONE', v)}
                  />
                </Grid>
              </>
            )}

            {venueFlags.hasTransportContact && (
              <>
                <Grid item xs={12} md={6}>
                  <FastInput
                    label="Transport Contact Name"
                    value={getVenueValue('TRANSPORT_CONTACT_NAME')}
                    onChange={v => handleVenueChange('TRANSPORT_CONTACT_NAME', v)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FastInput
                    label="Transport Contact Phone"
                    value={getVenueValue('TRANSPORT_CONTACT_PHONE')}
                    onChange={v => handleVenueChange('TRANSPORT_CONTACT_PHONE', v)}
                  />
                </Grid>
              </>
            )}

            {venueFlags.hasCateringContact && (
              <>
                <Grid item xs={12} md={6}>
                  <FastInput
                    label="Catering Contact Name"
                    value={getVenueValue('CATERING_CONTACT_NAME')}
                    onChange={v => handleVenueChange('CATERING_CONTACT_NAME', v)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FastInput
                    label="Catering Contact Phone"
                    value={getVenueValue('CATERING_CONTACT_PHONE')}
                    onChange={v => handleVenueChange('CATERING_CONTACT_PHONE', v)}
                  />
                </Grid>
              </>
            )}

            {venueFlags.hasMarketingContact && (
              <>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Marketing Contact Name"
                    value={getVenueValue('MARKETING_CONTACT_NAME')}
                    onChange={v => handleVenueChange('MARKETING_CONTACT_NAME', v)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Marketing Contact Email"
                    value={getVenueValue('MARKETING_CONTACT_EMAIL')}
                    onChange={v => handleVenueChange('MARKETING_CONTACT_EMAIL', v)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastInput
                    label="Marketing Contact Phone"
                    value={getVenueValue('MARKETING_CONTACT_PHONE')}
                    onChange={v => handleVenueChange('MARKETING_CONTACT_PHONE', v)}
                  />
                </Grid>
              </>
            )}
          </>
        )}

        {/* Route Planning to Next Venue - Context Aware */}
        {venueNumber < numberOfShows && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" sx={{ mb: 1, mt: 2 }}>
                <TransportIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Travel from {venueCity || `Venue ${venueNumber}`} to {nextVenueCity || `Venue ${venueNumber + 1}`}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <FastSelect
                label="Transport Type"
                value={getRouteValue(venueNumber + 1, 'TRANSPORT_TYPE')}
                onChange={v => handleRouteChange(venueNumber + 1, 'TRANSPORT_TYPE', v)}
                options={transportOptions}
                required
                error={errors[`VENUE_${venueNumber}_TO_VENUE_${venueNumber + 1}_TRANSPORT_TYPE`]}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FastInput
                label="Departure Time"
                type="time"
                value={getRouteValue(venueNumber + 1, 'DEPARTURE_TIME') || '12:00'}
                onChange={v => handleRouteChange(venueNumber + 1, 'DEPARTURE_TIME', v)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FastInput
                label="Arrival Time"
                type="time"
                value={getRouteValue(venueNumber + 1, 'ARRIVAL_TIME') || '15:00'}
                onChange={v => handleRouteChange(venueNumber + 1, 'ARRIVAL_TIME', v)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FastInput
                label="Travel Duration (hours)"
                value={getRouteValue(venueNumber + 1, 'DURATION') || '3'}
                onChange={v => handleRouteChange(venueNumber + 1, 'DURATION', v)}
                placeholder="3.5"
              />
            </Grid>
            <Grid item xs={12}>
              <FastInput
                label="Transport Details/Booking Reference"
                value={getRouteValue(venueNumber + 1, 'DETAILS')}
                onChange={v => handleRouteChange(venueNumber + 1, 'DETAILS', v)}
                placeholder="Flight number, train details, rental car booking, transfer company, etc."
              />
            </Grid>
          </>
        )}

        {/* Special Arrangements */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={venueFlags.hasSpecialArrangements}
                onChange={(e) => updateVenueFlag('hasSpecialArrangements', e.target.checked)}
                size="small"
              />
            }
            label="Special arrangements for this venue?"
          />
        </Grid>
        {venueFlags.hasSpecialArrangements && (
          <Grid item xs={12}>
            <FastInput
              label="Special Arrangements & Notes"
              value={getVenueValue('SPECIAL_ARRANGEMENTS')}
              onChange={v => handleVenueChange('SPECIAL_ARRANGEMENTS', v)}
              placeholder="Load-in requirements, parking, security notes, dietary requirements, etc."
            />
          </Grid>
        )}

        {/* Manager alerts */}
        {uiFlags.hasTourManager && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mt: 1 }}>
              <InfoIcon sx={{ mr: 0.5 }} />
              Contacts managed by tour manager
              {uiFlags.hasProductionManager && ' • Technical contacts managed by production manager'}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
});

const TourVariablesSection: React.FC<TourVariablesSectionProps> = ({
  variables,
  onVariableChange,
  errors = {},
  isInternational = false,
  onInternationalChange,
  scheduleId,
  dbState
}) => {
  const handleVariableChange = useCallback((key: string, value: any) => {
    onVariableChange(key, value);
  }, [onVariableChange]);

  // UI flags
  const [uiFlags, setUIFlags] = useState<any>(() => ({
    hasTourManager: variables.HAS_TOUR_MANAGER || false,
    hasProductionManager: variables.HAS_PRODUCTION_MANAGER || false,
    numberOfShows: Math.max(parseInt(variables.NUMBER_OF_SHOWS) || 2, 2),
    venueFlags: variables.UI_FLAGS?.venueFlags || {}
  }));

  const [currentVenueTab, setCurrentVenueTab] = useState(0);

useEffect(() => {
  // Only update if UI_FLAGS actually changed to prevent infinite loop
  const currentUIFlags = variables.UI_FLAGS;
  if (JSON.stringify(currentUIFlags) !== JSON.stringify(uiFlags)) {
    onVariableChange('UI_FLAGS', uiFlags);
  }
}, [uiFlags]); // Remove onVariableChange from dependencies

  const updateUIFlag = useCallback((key: string, value: any) => {
    setUIFlags((f: any) => ({ ...f, [key]: value }));
  }, []);

  // Date calculations
  const tourStartDate = variables.TOUR_START_DATE ? parseISO(variables.TOUR_START_DATE) : null;
  const tourEndDate = variables.TOUR_END_DATE ? parseISO(variables.TOUR_END_DATE) : null;

  const calculateTourDuration = useCallback((start: Date, end: Date): number => {
    return differenceInDays(end, start) + 1;
  }, []);

  const handleStartDateChange = useCallback((date: Date | null) => {
    if (date) {
      const startDateStr = format(date, 'yyyy-MM-dd');
      handleVariableChange('TOUR_START_DATE', startDateStr);
      
      // Auto-calculate end date if not set
      if (!variables.TOUR_END_DATE) {
        const defaultEndDate = addDays(date, 13); // 2 week default
        handleVariableChange('TOUR_END_DATE', format(defaultEndDate, 'yyyy-MM-dd'));
        handleVariableChange('TOUR_DURATION', '14');
      } else {
        // Recalculate duration
        const endDate = parseISO(variables.TOUR_END_DATE);
        const duration = calculateTourDuration(date, endDate);
        handleVariableChange('TOUR_DURATION', duration.toString());
      }
    }
  }, [variables.TOUR_END_DATE, handleVariableChange, calculateTourDuration]);

  const handleEndDateChange = useCallback((date: Date | null) => {
    if (date && tourStartDate) {
      const endDateStr = format(date, 'yyyy-MM-dd');
      handleVariableChange('TOUR_END_DATE', endDateStr);
      
      // Recalculate duration
      const duration = calculateTourDuration(tourStartDate, date);
      handleVariableChange('TOUR_DURATION', duration.toString());
    }
  }, [tourStartDate, handleVariableChange, calculateTourDuration]);

  const handleNumberOfShowsChange = useCallback((val: string) => {
    const num = Math.max(parseInt(val) || 2, 2);
    updateUIFlag('numberOfShows', num);
    handleVariableChange('NUMBER_OF_SHOWS', val);
    if (currentVenueTab >= num) {
      setCurrentVenueTab(0);
    }
  }, [updateUIFlag, handleVariableChange, currentVenueTab]);

  // Venue tabs
  const venueTabs = useMemo(() => 
    Array.from({ length: uiFlags.numberOfShows }, (_, i) => {
      const venueNumber = i + 1;
      const venueName = (variables as any)[`VENUE_${venueNumber}_NAME`];
      return {
        number: venueNumber,
        label: venueName ? `${venueNumber}. ${venueName}` : `Venue ${venueNumber}`
      };
    }), [uiFlags.numberOfShows, variables]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%', mt: 1 }}>
        {/* Database Status */}
        {scheduleId && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="success" sx={{ mb: 1 }}>
              Connected to database - changes auto-save every 3 seconds
            </Alert>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip label={`Schedule ID: ${scheduleId.slice(-8)}`} size="small" variant="outlined" />
              {dbState?.version && <Chip label={`Version: ${dbState.version}`} size="small" variant="outlined" />}
              <SaveStatusIndicator dbState={dbState} scheduleId={scheduleId} />
            </Box>
          </Box>
        )}

        {/* International Toggle */}
        {onInternationalChange && (
          <Accordion sx={{ mb: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">✈️ International Tour Options</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={isInternational}
                    onChange={e => onInternationalChange(e.target.checked)}
                  />
                }
                label="International Tour (includes arrival/departure logistics)"
              />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Tour Overview */}
        <Accordion defaultExpanded sx={{ mb: 1.5 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TourIcon />
              <Typography variant="h6">Tour Overview</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FastInput
                  label="Tour Name"
                  value={variables.TOUR_NAME || ''}
                  onChange={v => handleVariableChange('TOUR_NAME', v)}
                  required
                  error={!!errors.TOUR_NAME}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" error={!!errors.NUMBER_OF_SHOWS}>
                  <InputLabel>Number of Shows *</InputLabel>
                  <Select
                    value={variables.NUMBER_OF_SHOWS || '2'}
                    onChange={e => handleNumberOfShowsChange(e.target.value)}
                    label="Number of Shows *"
                  >
                    {Array.from({ length: 13 }, (_, i) => i + 2).map(n => (
                      <MenuItem key={n} value={n.toString()}>
                        {n} shows
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Tour Start Date *"
                  value={tourStartDate}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      required: true,
                      error: !!errors.TOUR_START_DATE
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Tour End Date"
                  value={tourEndDate}
                  onChange={handleEndDateChange}
                  minDate={tourStartDate ? addDays(tourStartDate, 1) : undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: !!errors.TOUR_END_DATE
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Tour Duration (days)"
                  value={variables.TOUR_DURATION || ''}
                  onChange={v => {
                    handleVariableChange('TOUR_DURATION', v);
                    // If user manually changes duration, update end date
                    if (tourStartDate && v) {
                      const days = parseInt(v);
                      if (days > 0) {
                        const newEndDate = addDays(tourStartDate, days - 1);
                        handleVariableChange('TOUR_END_DATE', format(newEndDate, 'yyyy-MM-dd'));
                      }
                    }
                  }}
                  placeholder="Calculated automatically"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Daily Allowance (per diem)"
                  value={variables.TOUR_DAILY_ALLOWANCE || ''}
                  onChange={v => handleVariableChange('TOUR_DAILY_ALLOWANCE', v)}
                  placeholder="€50, $75, etc."
                />
              </Grid>

              {/* Tour Team Switches */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, mt: 2 }}>
                  <PeopleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  Tour Team
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={uiFlags.hasTourManager}
                        onChange={e => {
                          updateUIFlag('hasTourManager', e.target.checked);
                          handleVariableChange('HAS_TOUR_MANAGER', e.target.checked);
                        }}
                      />
                    }
                    label="This tour has a Tour Manager"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={uiFlags.hasProductionManager}
                        onChange={e => {
                          updateUIFlag('hasProductionManager', e.target.checked);
                          handleVariableChange('HAS_PRODUCTION_MANAGER', e.target.checked);
                        }}
                      />
                    }
                    label="This tour has a Production Manager"
                  />
                </Box>
              </Grid>

              {/* Tour Manager Details */}
              {uiFlags.hasTourManager && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, mt: 1 }}>
                      Tour Manager Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FastInput
                      label="Tour Manager Name"
                      value={variables.TOUR_MANAGER || ''}
                      onChange={v => handleVariableChange('TOUR_MANAGER', v)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FastInput
                      label="Tour Manager Email"
                      value={variables.TOUR_MANAGER_EMAIL || ''}
                      onChange={v => handleVariableChange('TOUR_MANAGER_EMAIL', v)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FastInput
                      label="Tour Manager Phone"
                      value={variables.TOUR_MANAGER_PHONE || ''}
                      onChange={v => handleVariableChange('TOUR_MANAGER_PHONE', v)}
                    />
                  </Grid>
                </>
              )}

              {/* Production Manager Details */}
              {uiFlags.hasProductionManager && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, mt: 1 }}>
                      Production Manager Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FastInput
                      label="Production Manager Name"
                      value={variables.PRODUCTION_MANAGER || ''}
                      onChange={v => handleVariableChange('PRODUCTION_MANAGER', v)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FastInput
                      label="Production Manager Email"
                      value={variables.PRODUCTION_MANAGER_EMAIL || ''}
                      onChange={v => handleVariableChange('PRODUCTION_MANAGER_EMAIL', v)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FastInput
                      label="Production Manager Phone"
                      value={variables.PRODUCTION_MANAGER_PHONE || ''}
                      onChange={v => handleVariableChange('PRODUCTION_MANAGER_PHONE', v)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Venues with Route Planning */}
        <Accordion defaultExpanded sx={{ mb: 1.5 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon />
              <Typography variant="h6">Venues & Performance Details</Typography>
              <Chip label={`${uiFlags.numberOfShows} venues`} color="primary" size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Tabs 
              value={currentVenueTab} 
              onChange={(_, newValue) => setCurrentVenueTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              {venueTabs.map((tab, index) => (
                <Tab 
                  key={`tab-${tab.number}`} 
                  label={tab.label}
                />
              ))}
            </Tabs>

            {/* Active venue tab content */}
            {venueTabs.map((tab, index) => (
              currentVenueTab === index ? (
                <VenueTabContent
                  key={`venue-${tab.number}`}
                  venueNumber={tab.number}
                  variables={variables}
                  onVariableChange={handleVariableChange}
                  errors={errors}
                  uiFlags={uiFlags}
                  numberOfShows={uiFlags.numberOfShows}
                  tourStartDate={tourStartDate || undefined}
                  tourEndDate={tourEndDate || undefined}
                />
              ) : null
            ))}
          </AccordionDetails>
        </Accordion>

        {/* Tour Logistics */}
        <Accordion sx={{ mb: 1.5 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HotelIcon />
              <Typography variant="h6">Tour Logistics & Defaults</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  Global Defaults (can be overridden per venue)
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Default Soundcheck Duration (mins)"
                  value={variables.DEFAULT_SOUNDCHECK_DURATION || '60'}
                  onChange={v => handleVariableChange('DEFAULT_SOUNDCHECK_DURATION', v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Default Performance Duration (mins)"
                  value={variables.DEFAULT_PERFORMANCE_DURATION || '90'}
                  onChange={v => handleVariableChange('DEFAULT_PERFORMANCE_DURATION', v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Default Load-in Duration (mins)"
                  value={variables.DEFAULT_LOAD_IN_DURATION || '120'}
                  onChange={v => handleVariableChange('DEFAULT_LOAD_IN_DURATION', v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Default Load-out Duration (mins)"
                  value={variables.DEFAULT_LOAD_OUT_DURATION || '60'}
                  onChange={v => handleVariableChange('DEFAULT_LOAD_OUT_DURATION', v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Travel Buffer Time (hours)"
                  value={variables.TRAVEL_BUFFER_TIME || '1'}
                  onChange={v => handleVariableChange('TRAVEL_BUFFER_TIME', v)}
                  placeholder="1.5"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Preferred Hotel Chain"
                  value={variables.HOTEL_CHAIN_PREFERENCE || ''}
                  onChange={v => handleVariableChange('HOTEL_CHAIN_PREFERENCE', v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Default Check-in Time"
                  type="time"
                  value={variables.DEFAULT_CHECK_IN_TIME || '14:00'}
                  onChange={v => handleVariableChange('DEFAULT_CHECK_IN_TIME', v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Default Check-out Time"
                  type="time"
                  value={variables.DEFAULT_CHECK_OUT_TIME || '11:00'}
                  onChange={v => handleVariableChange('DEFAULT_CHECK_OUT_TIME', v)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FastInput
                  label="Accommodation Standard"
                  value={variables.ACCOMMODATION_STANDARD || ''}
                  onChange={v => handleVariableChange('ACCOMMODATION_STANDARD', v)}
                  placeholder="budget, mid-range, luxury, mixed"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Auto-Schedule Info */}
        <Alert severity="info">
          <AlertTitle>Auto-Schedule Generation</AlertTitle>
          Complete venue details and route planning to automatically generate your tour schedule.
          Route variables will control travel between venues.
          {isInternational && ' International logistics will be included.'}
          <br />
          <strong>Venue performance dates are restricted to your tour window: {tourStartDate && format(tourStartDate, 'MMM d')} - {tourEndDate && format(tourEndDate, 'MMM d, yyyy')}</strong>
        </Alert>
      </Box>
    </LocalizationProvider>
  );
};

export default TourVariablesSection;